/**
 * SessionProvider.tsx — who is signed in, and whether this client may operate.
 *
 * Two gates, in this order, before any of the workbench renders:
 *
 *   1. COMPATIBILITY. The boundary document requires the browser to declare
 *      the API range it supports and refuse to run outside it. A workbench that
 *      half-understands the server would draw plausible, wrong pictures.
 *
 *   2. AUTHENTICATION. There is no anonymous mode. The session is an HttpOnly
 *      cookie the script cannot read, so the only way to know whether one
 *      exists is to ask the server — which is exactly what `/api/v1/session`
 *      is for.
 *
 * Note what this component does NOT do: it never decides what the user may
 * change. Roles are shown here, and enforced server-side on every mutation
 * (§7.4). A capability hidden in the UI is a courtesy; a capability refused by
 * the server is the control.
 */

import { createContext, useContext, type ReactNode } from "react";
import { useClient } from "../api/ClientContext";
import { checkApiCompatibility } from "../api/compatibility";
import { describeError } from "../api/errors";
import { useAsync } from "../stores/useAsync";
import { SUPPORTED_API_RANGE, type ApplicationRole, type SessionView } from "../types/contract";

interface SessionContextValue {
  session: SessionView;
  reload: () => void;
  roleOn: (projectId: string) => ApplicationRole | undefined;
  /** Session membership is a client-side routing guard, never an authority grant. */
  canAccessProject: (projectId: string) => boolean;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside a SessionProvider.");
  return value;
}

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const client = useClient();
  const { data, error, loading, reload } = useAsync(() => client.session(), [client]);

  if (loading) return <Splash>Checking your session…</Splash>;

  if (error) {
    const described = describeError(error);
    return (
      <Splash tone="error">
        <h1>{described.title}</h1>
        <p>{described.detail}</p>
        {described.requestId && <p className="request-id">Request {described.requestId}</p>}
        <button type="button" onClick={reload}>Try again</button>
      </Splash>
    );
  }

  if (!data) return <Splash tone="error">The server returned no session document.</Splash>;

  const verdict = checkApiCompatibility(data.api.version, SUPPORTED_API_RANGE);
  if (!verdict.compatible) {
    return (
      <Splash tone="error">
        <h1>Incompatible API</h1>
        <p>{verdict.reason}</p>
        <p className="muted">
          This workbench refuses to operate outside its declared range rather than degrade unpredictably.
        </p>
      </Splash>
    );
  }

  if (!data.authenticated) {
    return (
      <Splash>
        <h1>PlantLifecycle</h1>
        <p>Sign in with your organisation account to open a project.</p>
        <a className="button" href={client.loginUrl(`/${window.location.hash || "#/"}`)}>Sign in</a>
        <p className="muted">API {data.api.version} · Engine {data.api.engineVersion}</p>
      </Splash>
    );
  }

  const memberships = new Map(data.projects.map(membership => [membership.projectId, membership.role]));

  return (
    <SessionContext.Provider value={{
      session: data,
      reload,
      roleOn: projectId => memberships.get(projectId),
      canAccessProject: projectId => memberships.has(projectId),
    }}>
      {children}
    </SessionContext.Provider>
  );
}

function Splash({ children, tone }: { children: ReactNode; tone?: "error" }): JSX.Element {
  return (
    <div className={`splash${tone === "error" ? " splash-error" : ""}`}>
      <div className="splash-card">{children}</div>
    </div>
  );
}
